import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/axios";
import { toast } from "react-toastify";
import {
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiX,
  FiCheck,
  FiCopy,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiXCircle,
} from "react-icons/fi";
import "./MetaAdsManagement.css";

const MetaAdsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [adStatusFilter, setAdStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");

  // Modal Detail State
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalAdStatus, setModalAdStatus] = useState("submitted");
  const [modalNotes, setModalNotes] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Copied state indicator
  const [copiedKey, setCopiedKey] = useState("");

  const fetchMetaAdRequests = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: pagination.limit,
        };
        if (search.trim()) params.search = search.trim();
        if (adStatusFilter !== "all") params.status = adStatusFilter;
        if (paymentStatusFilter !== "all") params.paymentStatus = paymentStatusFilter;

        const res = await api.get("/meta-ads/admin/requests", { params });

        if (res.data?.success) {
          setRequests(res.data.data || []);
          if (res.data.pagination) {
            setPagination((prev) => ({
              ...prev,
              page: res.data.pagination.page,
              total: res.data.pagination.total,
              totalPages: res.data.pagination.totalPages,
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching Meta Ads requests:", err);
        toast.error(err.response?.data?.message || "Failed to fetch Meta Ads requests");
      } finally {
        setLoading(false);
      }
    },
    [search, adStatusFilter, paymentStatusFilter, pagination.limit]
  );

  useEffect(() => {
    fetchMetaAdRequests(1);
  }, [fetchMetaAdRequests]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      fetchMetaAdRequests(newPage);
    }
  };

  const handleInlineStatusChange = async (requestId, newStatus) => {
    try {
      const res = await api.put(`/meta-ads/admin/update-status/${requestId}`, {
        adStatus: newStatus,
      });
      if (res.data?.success) {
        toast.success(`Status updated to "${newStatus}"`);
        // Update local list
        setRequests((prev) =>
          prev.map((item) => (item._id === requestId ? { ...item, adStatus: newStatus } : item))
        );
        if (selectedRequest && selectedRequest._id === requestId) {
          setSelectedRequest((prev) => ({ ...prev, adStatus: newStatus }));
        }
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error(err.response?.data?.message || "Failed to update ad status");
    }
  };

  const handleOpenDetails = (reqItem) => {
    setSelectedRequest(reqItem);
    setModalAdStatus(reqItem.adStatus || "submitted");
    setModalNotes(reqItem.adNotes || "");
  };

  const handleSaveModal = async () => {
    if (!selectedRequest) return;
    setUpdatingStatus(true);
    try {
      const res = await api.put(`/meta-ads/admin/update-status/${selectedRequest._id}`, {
        adStatus: modalAdStatus,
        notes: modalNotes,
      });
      if (res.data?.success) {
        toast.success("Ad request details updated successfully!");
        setRequests((prev) =>
          prev.map((item) =>
            item._id === selectedRequest._id
              ? { ...item, adStatus: modalAdStatus, adNotes: modalNotes }
              : item
          )
        );
        setSelectedRequest((prev) => ({
          ...prev,
          adStatus: modalAdStatus,
          adNotes: modalNotes,
        }));
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error("Error saving ad request:", err);
      toast.error(err.response?.data?.message || "Failed to update request");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const copyToClipboard = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.info("Copied to clipboard!");
    setTimeout(() => setCopiedKey(""), 2000);
  };

  const clearFilters = () => {
    setSearch("");
    setAdStatusFilter("all");
    setPaymentStatusFilter("all");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="meta-page">
      <div className="meta-content">
        {/* Header */}
        <div className="meta-header">
          <div className="meta-header-left">
            <h1>
              <span>📢 Meta Ads Requests & Payments</span>
              <span className="meta-header-badge">Admin Control</span>
            </h1>
          </div>
          <div className="meta-header-actions">
            <button
              className={`meta-refresh-btn ${loading ? "rotating" : ""}`}
              onClick={() => fetchMetaAdRequests(pagination.page)}
              title="Refresh records"
            >
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Main Section */}
        <div className="meta-section">
          {/* Filters Bar */}
          <div className="meta-filters-bar">
            <div className="meta-filter-group">
              {/* Search */}
              <div className="meta-search-box">
                <FiSearch className="meta-search-icon" />
                <input
                  type="text"
                  placeholder="Search by business, mobile, order ID, payment ID..."
                  className="meta-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchMetaAdRequests(1)}
                />
              </div>

              {/* Ad Status Filter */}
              <select
                className="meta-select"
                value={adStatusFilter}
                onChange={(e) => setAdStatusFilter(e.target.value)}
              >
                <option value="all">Ad Status: All</option>
                <option value="submitted">Submitted (New)</option>
                <option value="in_review">In Review</option>
                <option value="active">Active (Running)</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {/* Payment Status Filter */}
              <select
                className="meta-select"
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
              >
                <option value="all">Payment: All</option>
                <option value="paid">Paid (Verified)</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>

              {(search || adStatusFilter !== "all" || paymentStatusFilter !== "all") && (
                <button className="meta-clear-btn" onClick={clearFilters}>
                  <FiX /> Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="meta-table-wrap">
            <table className="meta-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Vendor / Business</th>
                  <th>Contact</th>
                  <th>Amount</th>
                  <th>Payment Status</th>
                  <th>Payment ID / Order ID</th>
                  <th>Campaign Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "40px" }}>
                      <FiRefreshCw className="rotating" style={{ fontSize: "24px", color: "#e9af66" }} />
                      <p style={{ marginTop: "10px", color: "#64748b" }}>Loading Meta Ads requests...</p>
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan="8">
                      <div className="meta-empty">
                        <div className="meta-empty-icon">📢</div>
                        <h4>No Meta Ads Requests Found</h4>
                        <p>There are no vendor advertising requests matching your filter criteria.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  requests.map((item) => {
                    const payStatus = item.paymentStatus || "pending";
                    const adStatus = item.adStatus || "submitted";
                    return (
                      <tr key={item._id}>
                        {/* Date */}
                        <td style={{ whiteSpace: "nowrap", color: "#64748b", fontSize: "12.5px" }}>
                          {formatDate(item.createdAt)}
                        </td>

                        {/* Business & Vendor */}
                        <td>
                          <div className="meta-business-title">
                            {item.businessName || item.vendor?.shop_name || "Vendor Business"}
                          </div>
                          <div className="meta-business-sub">
                            {item.category && <span className="meta-pill">{item.category}</span>}
                            {item.city && <span className="meta-pill">📍 {item.city}</span>}
                            {item.vendor?.name && (
                              <span style={{ color: "#64748b" }}>by {item.vendor.name}</span>
                            )}
                          </div>
                        </td>

                        {/* Contact */}
                        <td>
                          <div style={{ fontWeight: 600 }}>{item.mobileNumber || "—"}</div>
                          {item.email && (
                            <div style={{ fontSize: "12px", color: "#64748b" }}>{item.email}</div>
                          )}
                        </td>

                        {/* Amount */}
                        <td>
                          <span className="meta-amount">
                            ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                          </span>
                        </td>

                        {/* Payment Status */}
                        <td>
                          <span className={`badge-pay ${payStatus}`}>
                            {payStatus === "paid" && <FiCheck />}
                            {payStatus === "pending" && <FiClock />}
                            {payStatus === "failed" && <FiXCircle />}
                            {payStatus}
                          </span>
                        </td>

                        {/* Payment & Order IDs */}
                        <td>
                          {item.paymentId ? (
                            <div style={{ marginBottom: "4px" }}>
                              <span
                                className="meta-id-tag"
                                title="Click to copy Payment ID"
                                onClick={() => copyToClipboard(item.paymentId, `pay_${item._id}`)}
                              >
                                {copiedKey === `pay_${item._id}` ? <FiCheck color="#16a34a" /> : <FiCopy />}
                                {item.paymentId}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>No Payment ID</span>
                          )}

                          {item.orderId && (
                            <div>
                              <span
                                className="meta-id-tag"
                                style={{ background: "#f1f5f9" }}
                                title="Click to copy Order ID"
                                onClick={() => copyToClipboard(item.orderId, `order_${item._id}`)}
                              >
                                {copiedKey === `order_${item._id}` ? <FiCheck color="#16a34a" /> : <FiCopy />}
                                {item.orderId}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Campaign Status */}
                        <td>
                          <select
                            className="meta-status-dropdown"
                            value={adStatus}
                            onChange={(e) => handleInlineStatusChange(item._id, e.target.value)}
                          >
                            <option value="submitted">Submitted</option>
                            <option value="in_review">In Review</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "center" }}>
                          <button
                            className="meta-view-btn"
                            onClick={() => handleOpenDetails(item)}
                            title="View Full Details & Payment Receipt"
                          >
                            <FiEye /> View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="meta-pagination">
              <span>
                Showing page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong> ({pagination.total} total requests)
              </span>
              <div className="meta-page-buttons">
                <button
                  className="meta-page-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <FiChevronLeft /> Prev
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === pagination.totalPages ||
                      Math.abs(p - pagination.page) <= 1
                  )
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span style={{ padding: "4px 8px", color: "#94a3b8" }}>...</span>
                      )}
                      <button
                        className={`meta-page-btn ${pagination.page === p ? "active" : ""}`}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button
                  className="meta-page-btn"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details & Status Modal */}
      {selectedRequest && (
        <div className="meta-modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div
            className="meta-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="meta-modal-header">
              <h3>Meta Ads Request Details</h3>
              <button
                className="meta-modal-close"
                onClick={() => setSelectedRequest(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="meta-modal-body">
              {/* Top Banner Summary */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#fef9ed",
                  border: "1.5px dashed #f59e0b",
                  padding: "16px 20px",
                  borderRadius: "12px",
                }}
              >
                <div>
                  <div style={{ fontSize: "18px", fontWeight: 800, color: "#111827" }}>
                    {selectedRequest.businessName}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                    Submitted on {formatDate(selectedRequest.createdAt)}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "22px", fontWeight: 800, color: "#047857" }}>
                    ₹{Number(selectedRequest.amount || 0).toLocaleString("en-IN")}
                  </div>
                  <span className={`badge-pay ${selectedRequest.paymentStatus || "pending"}`}>
                    {selectedRequest.paymentStatus || "pending"}
                  </span>
                </div>
              </div>

              {/* Vendor Profile Section */}
              <div className="meta-modal-section">
                <div className="meta-modal-section-title">
                  🏪 Vendor Business Information
                </div>
                <div className="meta-grid-details">
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Business Name</span>
                    <span className="meta-detail-value">{selectedRequest.businessName}</span>
                  </div>
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Vendor Account</span>
                    <span className="meta-detail-value">
                      {selectedRequest.vendor?.name || "Unlinked / Guest"}
                    </span>
                  </div>
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Mobile Number</span>
                    <span className="meta-detail-value">
                      {selectedRequest.mobileNumber ? (
                        <a href={`tel:${selectedRequest.mobileNumber}`} style={{ color: "#e9af66", textDecoration: "none" }}>
                          📞 {selectedRequest.mobileNumber}
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Email Address</span>
                    <span className="meta-detail-value">
                      {selectedRequest.email || selectedRequest.vendor?.email || "—"}
                    </span>
                  </div>
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Category</span>
                    <span className="meta-detail-value">{selectedRequest.category || "—"}</span>
                  </div>
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">City / Location</span>
                    <span className="meta-detail-value">{selectedRequest.city || "—"}</span>
                  </div>
                </div>
              </div>

              {/* Razorpay Payment Details Section */}
              <div className="meta-modal-section">
                <div className="meta-modal-section-title">
                  💳 Razorpay Payment & Transaction Details
                </div>
                <div className="meta-grid-details">
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Amount Paid</span>
                    <span className="meta-detail-value" style={{ color: "#047857", fontSize: "16px" }}>
                      ₹{Number(selectedRequest.amount || 0).toLocaleString("en-IN")} {selectedRequest.currency || "INR"}
                    </span>
                  </div>
                  <div className="meta-detail-item">
                    <span className="meta-detail-label">Payment Verification</span>
                    <span className="meta-detail-value">
                      <span className={`badge-pay ${selectedRequest.paymentStatus || "pending"}`}>
                        {selectedRequest.paymentStatus === "paid" ? "✅ Verified Paid" : selectedRequest.paymentStatus}
                      </span>
                    </span>
                  </div>
                  <div className="meta-detail-item" style={{ gridColumn: "span 2" }}>
                    <span className="meta-detail-label">Razorpay Payment ID</span>
                    <span className="meta-detail-value">
                      {selectedRequest.paymentId ? (
                        <span
                          className="meta-id-tag"
                          onClick={() => copyToClipboard(selectedRequest.paymentId, "modal_pay")}
                        >
                          <FiCopy /> {selectedRequest.paymentId}
                        </span>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <div className="meta-detail-item" style={{ gridColumn: "span 2" }}>
                    <span className="meta-detail-label">Razorpay Order ID</span>
                    <span className="meta-detail-value">
                      {selectedRequest.orderId ? (
                        <span
                          className="meta-id-tag"
                          onClick={() => copyToClipboard(selectedRequest.orderId, "modal_order")}
                        >
                          <FiCopy /> {selectedRequest.orderId}
                        </span>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  {selectedRequest.signature && (
                    <div className="meta-detail-item" style={{ gridColumn: "span 2" }}>
                      <span className="meta-detail-label">Payment Signature (HMAC)</span>
                      <span
                        className="meta-detail-value"
                        style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace" }}
                      >
                        {selectedRequest.signature}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign Management Form */}
              <div className="meta-modal-section" style={{ background: "#ffffff" }}>
                <div className="meta-modal-section-title">
                  🎯 Campaign Status & Management
                </div>
                <div style={{ marginBottom: "14px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: "#334155",
                      marginBottom: "6px",
                    }}
                  >
                    Campaign Status
                  </label>
                  <select
                    className="meta-select"
                    style={{ width: "100%" }}
                    value={modalAdStatus}
                    onChange={(e) => setModalAdStatus(e.target.value)}
                  >
                    <option value="submitted">Submitted (New request received)</option>
                    <option value="in_review">In Review (Reviewing ad creatives & audience)</option>
                    <option value="active">Active (Campaign live on Facebook & Instagram)</option>
                    <option value="completed">Completed (Campaign budget spent & finished)</option>
                    <option value="cancelled">Cancelled / Refunded</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: "#334155",
                      marginBottom: "6px",
                    }}
                  >
                    Admin Internal Notes / Campaign Links
                  </label>
                  <textarea
                    className="meta-notes-textarea"
                    placeholder="Add campaign remarks, Meta Ads Manager link, targeting notes, or communication log..."
                    value={modalNotes}
                    onChange={(e) => setModalNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="meta-modal-footer">
              <button
                type="button"
                className="meta-modal-btn-cancel"
                onClick={() => setSelectedRequest(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="meta-modal-btn-save"
                onClick={handleSaveModal}
                disabled={updatingStatus}
              >
                {updatingStatus ? <FiRefreshCw className="rotating" /> : <FiCheck />}
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaAdsManagement;
